import { Timeouts } from '@gap/utils/date-utils'
import { namingConfig } from '@gap/utils/config/naming-config'
import { makeAutoObservable } from 'mobx'
import type DisposableReactionsStore from 'reusable/mobx-store-utils/DisposableReactionsStore'
import type { IStoreWithReactions } from 'reusable/mobx-store-utils/useStore'
import { snackbarWarning } from 'reusable/mui/snackbar/snackbar'
import { WEBSOCKET_GRACE_MS } from './ServerReachabilityStore'

/** How long the server stays unreachable before the follow-up warning, matching the server's lock expiry. */
export const UNREACHABLE_FOLLOW_UP_MS = Timeouts.minute

/** How long after the browser comes back online before an outage is warned about again. */
export const ONLINE_SETTLE_MS = WEBSOCKET_GRACE_MS

const messages = {
   retrying: `We're having trouble reaching ${namingConfig.product.singular}. Retrying...`,
   stillUnreachable: `We're still having trouble reaching ${namingConfig.product.singular}. Retrying...`,
}

type ServerReachabilityNotifierParams = {
   reachabilityStore: { unreachableSince: number | null }
}

/** Warns the user by snackbar when the server cannot be reached, and again if it stays unreachable. */
export default class ServerReachabilityNotifier implements IStoreWithReactions {
   sp: ServerReachabilityNotifierParams
   isBrowserOnline = globalThis.navigator?.onLine !== false

   private followUpTimer: ReturnType<typeof setTimeout> | null = null
   private onlineSettleTimer: ReturnType<typeof setTimeout> | null = null

   constructor(sp: ServerReachabilityNotifierParams) {
      this.sp = sp
      makeAutoObservable<ServerReachabilityNotifier, 'followUpTimer' | 'onlineSettleTimer'>(this, {
         sp: false,
         followUpTimer: false,
         onlineSettleTimer: false,
      }, { autoBind: true })
   }

   /** True while the server is unreachable and the offline banner is not already showing. */
   get shouldWarn() {
      return this.isBrowserOnline && this.sp.reachabilityStore.unreachableSince !== null
   }

   configureReactions(reactions: DisposableReactionsStore) {
      window.addEventListener('online', this.onBrowserOnline)
      window.addEventListener('offline', this.onBrowserOffline)
      reactions.registerReaction({
         observedExpression: () => this.shouldWarn,
         reaction: this.onShouldWarnChanged,
         fireImmediately: true,
      })
      reactions.registerDisposer(this.dispose)
   }

   private onShouldWarnChanged(shouldWarn: boolean) {
      this.cancelFollowUp()
      if (!shouldWarn) return
      snackbarWarning(messages.retrying)
      this.followUpTimer = setTimeout(this.followUp, UNREACHABLE_FOLLOW_UP_MS)
   }

   private followUp() {
      this.followUpTimer = null
      snackbarWarning(messages.stillUnreachable)
   }

   private onBrowserOnline() {
      this.cancelOnlineSettle()
      this.onlineSettleTimer = setTimeout(this.settleOnline, ONLINE_SETTLE_MS)
   }

   private settleOnline() {
      this.onlineSettleTimer = null
      this.isBrowserOnline = true
   }

   private onBrowserOffline() {
      this.cancelOnlineSettle()
      this.isBrowserOnline = false
   }

   private cancelFollowUp() {
      if (this.followUpTimer) clearTimeout(this.followUpTimer)
      this.followUpTimer = null
   }

   private cancelOnlineSettle() {
      if (this.onlineSettleTimer) clearTimeout(this.onlineSettleTimer)
      this.onlineSettleTimer = null
   }

   private dispose() {
      window.removeEventListener('online', this.onBrowserOnline)
      window.removeEventListener('offline', this.onBrowserOffline)
      this.cancelFollowUp()
      this.cancelOnlineSettle()
   }
}
